import { convertToMinutes, isWeekday, normalizeDateInput } from "./utils";
import logger from "./logger";

/** Default “before 6 PM” cutoff: 18:00. Exactly 6:00 PM is not “before” (uses default rate). */
export const DEFAULT_COMMISSION_EVENING_CUTOFF_MINUTES = 18 * 60;

const WEEKDAY_SHORT = new Set(["Mon", "Tue", "Wed", "Thu", "Fri"]);

/**
 * @param {Date} date - Any instant on the calendar day to evaluate (e.g. from normalizeDateInput).
 * @param {string} timeZone - IANA time zone (e.g. "Asia/Singapore").
 * @returns {boolean}
 */
export function isWeekdayInTimeZone(date, timeZone) {
	if (!(date instanceof Date) || isNaN(date.getTime())) return false;
	try {
		const short = new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short" }).format(date);
		return WEEKDAY_SHORT.has(short);
	} catch {
		return isWeekday(date);
	}
}

/**
 * Normalize booking time to a string `convertToMinutes` accepts (24h or 12h with AM/PM).
 * @param {string|{ value?: string, label?: string }|undefined|null} bookingTime
 * @returns {string}
 */
export function coerceBookingTimeToString(bookingTime) {
	if (bookingTime == null) return "";
	if (typeof bookingTime === "string") return bookingTime.trim();
	if (typeof bookingTime === "object") {
		const v = bookingTime.value ?? bookingTime.label ?? "";
		return typeof v === "string" ? v.trim() : "";
	}
	return "";
}

/**
 * Chooses commission rate for checkout: only on **weekdays**, **before** the configured evening
 * cutoff, when a **discount** applies and the day is **not** a public holiday, uses the Member OP
 * rate. All other cases use the default rate (discount ignored for commission).
 *
 * **6:00 PM edge:** `cutoffMinutesFromMidnight` defaults to 1080 (18:00). Times strictly *before* 1080
 * minutes count as “before 6 PM”; 18:00:00 uses the default rate.
 *
 * **Timezone:** When `options.timeZone` is set, weekday is computed for `bookingDate`’s calendar day
 * in that zone (via `normalizeDateInput` + `Intl`). The time comparison still uses
 * `coerceBookingTimeToString(bookingTime)` as wall-clock minutes — pass times already in the
 * outlet’s local convention, or pre-adjust `bookingTime` before calling.
 *
 * @param {Object} input
 * @param {Date|string} input.bookingDate
 * @param {string|{ value?: string, label?: string }} [input.bookingTime]
 * @param {boolean} input.isDiscountApplied
 * @param {boolean} input.isPublicHoliday
 * @param {number} input.defaultCommissionRate - Tier “normal” rate when discount does not force OP.
 * @param {number} input.memberOpCommissionRate - Member OP (before-6-style) rate when discount applies.
 * @param {Object} [options]
 * @param {number} [options.cutoffMinutesFromMidnight=1080] - Exclusive upper bound (e.g. 18*60 → before 6 PM).
 * @param {string} [options.timeZone] - IANA zone for weekday; omit for local `isWeekday(bookingDate)`.
 * @returns {number} finalCommissionRate
 */
export function resolveFinalCommissionRate(input, options = {}) {
	const {
		bookingDate,
		bookingTime,
		isDiscountApplied,
		isPublicHoliday,
		defaultCommissionRate,
		memberOpCommissionRate,
	} = input;

	logger.log("commissionChange >> bookingTime", bookingTime);
	logger.log("commissionChange >> bookingTime", bookingTime);
	logger.log("commissionChange >> isDiscountApplied", isDiscountApplied);
	logger.log("commissionChange >> isPublicHoliday", isPublicHoliday);
	logger.log("commissionChange >> defaultCommissionRate", defaultCommissionRate);
	logger.log("commissionChange >> memberOpCommissionRate", memberOpCommissionRate);

	const cutoffMinutes =
		options.cutoffMinutesFromMidnight ?? DEFAULT_COMMISSION_EVENING_CUTOFF_MINUTES;
	const timeZone = options.timeZone;

	const safeDefault = Number(defaultCommissionRate);
	const safeOp = Number(memberOpCommissionRate);
	const baseDefault = Number.isFinite(safeDefault) ? safeDefault : 0;
	const baseOp = Number.isFinite(safeOp) ? safeOp : baseDefault;

	if (isPublicHoliday) return baseDefault;

	const normalized = normalizeDateInput(bookingDate);
	if (!normalized) return baseDefault;

	const weekday =
		timeZone != null && timeZone !== ""
			? isWeekdayInTimeZone(normalized, timeZone)
			: isWeekday(bookingDate);

	if (!weekday) return baseDefault;

	const timeStr = coerceBookingTimeToString(bookingTime);
	const minutes = timeStr ? convertToMinutes(timeStr) : 0;
	const isStrictlyBeforeCutoff = minutes < cutoffMinutes;

	if (isStrictlyBeforeCutoff && isDiscountApplied) return baseOp;
	return baseDefault;
}
